import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { 
    ArrowLeft, 
    MapPin, 
    Building2, 
    Globe, 
    Loader2, 
    Phone, 
    Users, 
    Briefcase,
    Mail,
    Edit2,
    Trash2
} from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";
import { ui } from "../config/theme";


const CompanyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clusters, setClusters] = useState([]);
    const [units, setUnits] = useState([]);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/companies/${id}`);
                if (!response.ok) throw new Error('Company not found');
                const data = await response.json();
                setCompany(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchCompany();
    }, [id]);

    // Load clusters and units for this company
    useEffect(() => {
        (async () => {
            if (!id) return;
            try {
                const [clRes, unRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/companies/${id}/clusters`),
                    fetch(`${API_BASE_URL}/api/companies/${id}/units`),
                ]);
                const clData = clRes.ok ? await clRes.json() : [];
                const unData = unRes.ok ? await unRes.json() : [];
                setClusters(Array.isArray(clData) ? clData : []);
                setUnits(Array.isArray(unData) ? unData : []);
            } catch {
                setClusters([]);
                setUnits([]);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className={`${ui.sectionCard} p-8 flex items-center justify-center`}>
                            <div className="flex items-center gap-2 text-gray-600">
                                <Loader2 className="w-6 h-6 animate-spin" />
                                <span>Loading company details...</span>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className={`${ui.sectionCard} p-8 text-center`}>
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Get company name from either name or company_name field
    const companyName = company?.name || company?.company_name || 'Unknown Company';
    const companyWebsite = company?.website || company?.link;
    const clusterNameById = (cid) => clusters.find(c => String(c.id) === String(cid))?.name || '';

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className={ui.sectionCard}>
                        <div className="p-6 md:p-8">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
                            >
                                <ArrowLeft className="w-5 h-5 mr-1" />
                                Back to Companies
                            </button>
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-8 text-white rounded-xl mb-8">
                                <div className="flex flex-col sm:flex-row items-center gap-6">
                                    {company.logo ? (
                                        <img 
                                            src={`${API_BASE_URL}/api/companies/${company.id}/logo`}
                                            alt={`${companyName} Logo`} 
                                            className="w-24 h-24 rounded-xl bg-white p-2 shadow-lg object-cover" 
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div className="w-24 h-24 rounded-xl bg-white/10 flex items-center justify-center" style={{ display: company.logo ? 'none' : 'flex' }}>
                                        <Building2 className="w-12 h-12" />
                                    </div>
                                    <div className="text-center sm:text-left">
                                        <h1 className="text-3xl font-semibold mb-2">{companyName}</h1>
                                        {company.location && (
                                            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                                                <MapPin className="w-5 h-5" />
                                                <span>{company.location}</span>
                                            </div>
                                        )}
                                        {company.industry && (
                                            <div className="flex items-center justify-center sm:justify-start gap-2">
                                                <Briefcase className="w-5 h-5" />
                                                <span>{company.industry}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Structure: Clusters & Units (if available) */}
                            {(clusters.length > 0 || units.length > 0) && (
                              <div className="mb-8">
                                <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
                                  Structure
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  {clusters.length > 0 && (
                                    <div className="bg-white rounded-xl border p-4">
                                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Clusters</h3>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full text-left text-sm">
                                          <thead>
                                            <tr className="text-gray-600">
                                              <th className="py-2 px-2">Name</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {clusters.map(cl => (
                                              <tr key={cl.id} className="border-t">
                                                <td className="py-2 px-2 text-gray-800">{cl.name}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                  {units.length > 0 && (
                                    <div className="bg-white rounded-xl border p-4">
                                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Units</h3>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full text-left text-sm">
                                          <thead>
                                            <tr className="text-gray-600">
                                              <th className="py-2 px-2">Name</th>
                                              <th className="py-2 px-2">Cluster</th>
                                              <th className="py-2 px-2">Phone</th>
                                              <th className="py-2 px-2">Address</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {units.map(u => (
                                              <tr key={u.id} className="border-t">
                                                <td className="py-2 px-2 text-gray-800">{u.name}</td>
                                                <td className="py-2 px-2 text-gray-800">{u.cluster_id ? clusterNameById(u.cluster_id) : '-'}</td>
                                                <td className="py-2 px-2 text-gray-800">{u.phone || '-'}</td>
                                                <td className="py-2 px-2 text-gray-800">{u.address || '-'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Company Description */}
                            {company.description && (
                                <div className="mb-8">
                                    <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
                                        <Globe className="w-5 h-5" />
                                        About {companyName}
                                    </div>
                                    <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                                        {company.description}
                                    </p>
                                </div>
                            )}

                            {/* Company Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                {/* Contact Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                                    
                                    {company.phone && (
                                        <div className="flex items-center gap-3">
                                            <Phone className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-700">{company.phone}</span>
                                        </div>
                                    )}
                                    
                                    {company.email && (
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-700">{company.email}</span>
                                        </div>
                                    )}
                                    
                                    {company.address && (
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                                            <span className="text-gray-700">{company.address}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Company Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Company Information</h3>
                                    
                                    {company.employee_count && (
                                        <div className="flex items-center gap-3">
                                            <Users className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-700">{company.employee_count} employees</span>
                                        </div>
                                    )}
                                    
                                    {company.founded_year && (
                                        <div className="flex items-center gap-3">
                                            <Building2 className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-700">Founded in {company.founded_year}</span>
                                        </div>
                                    )}
                                    
                                    {company.revenue && (
                                        <div className="flex items-center gap-3">
                                            <Briefcase className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-700">Revenue: {company.revenue}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Website Link */}
                            {companyWebsite && (
                                <div className="border-t pt-6">
                                    <a 
                                        href={companyWebsite.startsWith('http') ? companyWebsite : `https://${companyWebsite}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        <Globe className="w-5 h-5" />
                                        Visit Website
                                        <span className="ml-1">→</span>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default CompanyDetails;